import { useState } from 'react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useCommunityContext } from '@/providers/community-context';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { SocialView } from './social-view';
import { BusinessView } from './business-view';
import { ProjectsView } from './projects-view';
import { Loader2, Users, Briefcase, FolderKanban, ArrowRight, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SegmentedTabItem } from '@/components/ui/segmented-tabs';
import type { AdvertisementCampaign } from '@/types';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '@/hooks/use-user';
import { AdvertisementBannerCarousel } from '@/components/advertisements/AdvertisementBannerCarousel';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

const DASHBOARD_VIEWS = ['social', 'business', 'projects'] as const;
type DashboardView = (typeof DASHBOARD_VIEWS)[number];

function viewFromSearchParam(
  requested: string | null,
  community: { isBusinessEnabled?: boolean; isProjectDashboardEnabled?: boolean }
): DashboardView | null {
  if (requested === 'projects' && community.isProjectDashboardEnabled) return 'projects';
  if (requested === 'social') return 'social';
  if (requested === 'business' && community.isBusinessEnabled) return 'business';
  return null;
}

function defaultView(community: {
  isBusinessEnabled?: boolean;
  isProjectDashboardEnabled?: boolean;
}): DashboardView {
  if (community.isBusinessEnabled) return 'business';
  if (community.isProjectDashboardEnabled) return 'projects';
  return 'social';
}

function HeaderControls<T extends string>({
  items,
  value,
  onValueChange,
  communityId,
  hasNewSocialPost,
}: {
  items: SegmentedTabItem<T>[];
  value: T;
  onValueChange: (v: T) => void;
  communityId?: string;
  hasNewSocialPost?: boolean;
}) {
  const navigate = useNavigate();

  const visible = items.filter((i) => !i.hidden);
  const active = visible.find((i) => i.value === value);

  return (
    <div className="flex items-center justify-between gap-3 w-[calc(100%+2rem)] -ml-4 px-4 py-3 -mt-4 rounded-none border-x-0 border-t-0 border-b border-border bg-card shadow-sm sm:w-full sm:ml-0 sm:mt-0 sm:rounded-2xl sm:border-x sm:border-t">
      {active &&
        (() => {
          const Icon = active.icon;
          return (
            <div className="flex items-center gap-2 text-primary truncate">
              {Icon && <Icon className="h-5 w-5 shrink-0" />}
              <span className="text-base font-bold truncate">{active.label}</span>
            </div>
          );
        })()}

      <div className="flex items-center gap-2 shrink-0">
        {value === 'social' && (
          <button
            type="button"
            onClick={() => {
              if (communityId) {
                navigate(`/${communityId}/search`);
              }
            }}
            className="flex items-center justify-center w-9 h-9 rounded-full border border-border bg-white dark:bg-zinc-800 text-muted-foreground shadow-sm transition-colors hover:border-primary/40 hover:text-primary shrink-0"
            aria-label="Search posts"
            title="Search posts"
          >
            <Search className="h-4 w-4" />
          </button>
        )}
        {visible.map((item) => {
          const Icon = item.icon;
          const isActive = item.value === value;
          const showBadge = item.value === 'social' && hasNewSocialPost && !isActive;
          return (
            <div key={item.value} className="relative inline-flex">
              <button
                type="button"
                onClick={() => onValueChange(item.value)}
                className={cn(
                  'flex items-center justify-center w-9 h-9 rounded-full border shadow-sm transition-all',
                  isActive
                    ? 'border-primary/50 bg-primary/10 text-primary font-medium'
                    : 'border-border bg-white dark:bg-zinc-800 text-muted-foreground hover:border-primary/40 hover:text-primary'
                )}
                aria-label={`${item.label} view`}
                title={`${item.label} view`}
              >
                {Icon && <Icon className="h-4 w-4 shrink-0" />}
              </button>
              {showBadge && (
                <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3 pointer-events-none">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function CommunityDashboardPage() {
  const { community: currentCommunity, isLoading } = useCommunityContext();
  const { user } = useUser();
  const [viewParam, setViewParam] = useQueryState('view', parseAsStringLiteral(DASHBOARD_VIEWS));
  const [userView, setUserView] = useState<DashboardView | null>(
    () => (localStorage.getItem('dashboardView') as DashboardView | null) ?? null
  );

  const userCommunity = user?.communities.find((c) => c.id === currentCommunity?.id);
  const userRoles = userCommunity?.roles || [];
  const isAdminOrCreator = userRoles.some((role) => ['ADMIN', 'CREATOR', 'OWNER'].includes(role));

  const { data: unreadData, mutate: mutateUnread } = useSWR<{
    count: number;
    postIds: string[];
  }>(
    currentCommunity?.id ? `/api/communities/${currentCommunity.id}/posts/unread` : null,
    (url) => axiosInstance.get(url).then((res) => res.data),
    { refreshInterval: 60000 }
  );

  const { data: adsData } = useSWR(
    currentCommunity?.id && currentCommunity.isAdvertisementEnabled
      ? `/api/communities/${currentCommunity.id}/advertisement-campaigns/active?type=DASHBOARD`
      : null,
    (url) => axiosInstance.get(url).then((res) => res.data)
  );
  const activeCampaigns: AdvertisementCampaign[] = adsData?.campaigns ?? [];

  const { data: requestsData } = useSWR(
    isAdminOrCreator && currentCommunity?.id
      ? `/api/communities/${currentCommunity.id}/requests`
      : null,
    (url) => axiosInstance.get(url).then((res) => res.data)
  );
  const pendingRequestsCount = requestsData?.requests?.length ?? 0;

  const urlView = viewFromSearchParam(viewParam, currentCommunity || { isBusinessEnabled: false });
  let effectiveViewMode: DashboardView =
    urlView ?? userView ?? defaultView(currentCommunity || { isBusinessEnabled: false });

  const hasNewSocialPost = Boolean(
    (unreadData?.count ?? 0) > 0 && effectiveViewMode !== 'social'
  );

  if (isLoading)
    return (
      <div className="flex items-center justify-center p-24">
        <Loader2 className="h-10 w-10 animate-spin text-primary opacity-50" />
      </div>
    );

  if (!currentCommunity) return null;

  if (effectiveViewMode === 'business' && !currentCommunity.isBusinessEnabled) {
    effectiveViewMode = currentCommunity.isProjectDashboardEnabled ? 'projects' : 'social';
  }
  if (effectiveViewMode === 'projects' && !currentCommunity.isProjectDashboardEnabled) {
    effectiveViewMode = currentCommunity.isBusinessEnabled ? 'business' : 'social';
  }

  const dashboardTabItems: SegmentedTabItem<DashboardView>[] = [
    { value: 'social', label: 'Social', icon: Users },
    {
      value: 'business',
      label: 'Business',
      icon: Briefcase,
      hidden: !currentCommunity.isBusinessEnabled,
    },
    {
      value: 'projects',
      label: 'Projects',
      icon: FolderKanban,
      hidden: !currentCommunity.isProjectDashboardEnabled,
    },
  ];

  return (
    <div className="space-y-4 pb-12">
      {/* Advertisement Banner */}
      {currentCommunity.isAdvertisementEnabled && activeCampaigns.length > 0 && (
        <AdvertisementBannerCarousel
          communityId={currentCommunity.id}
          campaigns={activeCampaigns}
        />
      )}
      {currentCommunity.banner ? (
        <div className="-mx-4 w-[calc(100%+2rem)] sm:mx-0 sm:w-full">
          <img
            src={currentCommunity.banner}
            alt={`${currentCommunity.name} banner`}
            className="block h-auto w-full max-h-56 object-contain object-center sm:max-h-64 md:max-h-72 lg:max-h-80"
          />
        </div>
      ) : null}

      {isAdminOrCreator && pendingRequestsCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-2xl p-6 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center text-primary shrink-0">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-foreground tracking-tight font-outfit">
                Pending Member Approvals
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                There {pendingRequestsCount === 1 ? 'is' : 'are'}{' '}
                <span className="font-semibold text-primary">{pendingRequestsCount}</span> request
                {pendingRequestsCount === 1 ? '' : 's'} waiting for approval.
              </p>
            </div>
          </div>
          <Button
            asChild
            className="rounded-xl bg-primary text-white hover:bg-primary/90 shadow-md shadow-primary/20 shrink-0"
          >
            <Link to={`/${currentCommunity.id}/requests`} className="flex items-center gap-2">
              Review Requests
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </motion.div>
      )}

      <HeaderControls
        value={effectiveViewMode}
        onValueChange={(v) => {
          setUserView(v);
          localStorage.setItem('dashboardView', v);
          void setViewParam(v);
        }}
        items={dashboardTabItems}
        communityId={currentCommunity.id}
        hasNewSocialPost={hasNewSocialPost}
      />

      <div>
        <div className={cn(effectiveViewMode === 'social' ? 'block' : 'hidden')}>
          <SocialView
            community={currentCommunity}
            unreadPostIds={unreadData?.postIds ?? []}
            onUnreadViewed={(postId) => {
              void mutateUnread(
                (current) => {
                  if (!current) return current;
                  return {
                    count: Math.max(0, current.count - (current.postIds.includes(postId) ? 1 : 0)),
                    postIds: current.postIds.filter((id) => id !== postId),
                  };
                },
                { revalidate: false }
              );
            }}
          />
        </div>
        {effectiveViewMode === 'projects' ? (
          <ProjectsView />
        ) : effectiveViewMode === 'business' ? (
          <BusinessView />
        ) : null}
      </div>
    </div>
  );
}
